"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { UserMenu } from "@/components/UserMenu";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
interface SiteNavbarSession {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
}
interface SiteNavbarProps {
  session?: SiteNavbarSession | null;
  rightSlot?: React.ReactNode;
  /** Shows the Admin link in the account menu. */
  isAdmin?: boolean;
}

// Top coins for the live ticker strip
const TICKER_COINS = ["BTCUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT", "XRPUSDT", "ADAUSDT", "DOGEUSDT", "DOTUSDT", "AVAXUSDT", "LINKUSDT"];
const SYMBOL_LABELS: Record<string, string> = {
  BTCUSDT: "BTC",
  ETHUSDT: "ETH",
  SOLUSDT: "SOL",
  BNBUSDT: "BNB",
  XRPUSDT: "XRP",
  ADAUSDT: "ADA",
  DOGEUSDT: "DOGE",
  DOTUSDT: "DOT",
  AVAXUSDT: "AVAX",
  LINKUSDT: "LINK"
};
interface TickerPrice {
  symbol: string;
  price: number;
  changePercent: number;
}
const navLinks = [{
  href: "/token",
  label: "Tokens",
  icon: "M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
}, {
  href: "/chain",
  label: "Chains",
  icon: "M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
}, {
  href: "/developer-activity",
  label: "Dev Activity",
  icon: "M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5"
}, {
  href: "/fear-greed",
  label: "Fear & Greed",
  icon: "M3.75 3v11.25A2.25 2.25 0 006 16.5h2.25M3.75 3h-1.5m1.5 0h16.5m0 0h1.5m-1.5 0v11.25A2.25 2.25 0 0118 16.5h-2.25m-7.5 0h7.5m-7.5 0l-1 3m8.5-3l1 3m0 0l.5 1.5m-.5-1.5h-9.5m0 0l-.5 1.5m.75-9l3-3 2.148 2.148A12.061 12.061 0 0116.5 7.605"
}];
function useLiveTicker() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = new Map();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [prices, setPrices] = useState(t0);
  const wsRef = useRef(null);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const streams = TICKER_COINS.map(_temp).join("/");
      const ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
      wsRef.current = ws;
      ws.onmessage = event => {
        const msg = JSON.parse(event.data as string) as {
          data: {
            s: string;
            c: string;
            P: string;
          };
        };
        const d = msg.data;
        const price = parseFloat(d.c);
        const changePercent = parseFloat(d.P);
        if (isNaN(price)) {
          return;
        }
        setPrices(prev => {
          const next = new Map(prev);
          next.set(d.s, {
            symbol: d.s,
            price,
            changePercent: isNaN(changePercent) ? 0 : changePercent
          });
          return next;
        });
      };
      return () => {
        ws.close();
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return prices;
}
function _temp(s) {
  return `${s.toLowerCase()}@ticker`;
}
function formatTickerPrice(price: number): string {
  if (price < 1) return `$${price.toFixed(4)}`;
  if (price < 100) return `$${price.toFixed(2)}`;
  return `$${price.toLocaleString(undefined, {
    maximumFractionDigits: 0
  })}`;
}
export function SiteNavbar(t0) {
  const $ = _c(49);
  const {
    session: t1,
    rightSlot,
    isAdmin: t2
  } = t0;
  const session = t1 === undefined ? null : t1;
  const isAdmin = t2 === undefined ? false : t2;
  const [mounted, setMounted] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();
  const prices = useLiveTicker();
  let t3;
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      setMounted(true);
    };
    t4 = [];
    $[0] = t3;
    $[1] = t4;
  } else {
    t3 = $[0];
    t4 = $[1];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const handleScroll = () => setScrolled(window.scrollY > 10);
      window.addEventListener("scroll", handleScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", handleScroll);
    };
    t6 = [];
    $[2] = t5;
    $[3] = t6;
  } else {
    t5 = $[2];
    t6 = $[3];
  }
  useEffect(t5, t6);
  let t7;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      setMobileOpen(false);
    };
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  let t8;
  if ($[5] !== pathname) {
    t8 = [pathname];
    $[5] = pathname;
    $[6] = t8;
  } else {
    t8 = $[6];
  }
  useEffect(t7, t8);
  let t9;
  if ($[7] !== mounted || $[8] !== prices) {
    t9 = TICKER_COINS.map(sym => {
      const data = mounted ? prices.get(sym) : undefined;
      const label = SYMBOL_LABELS[sym] ?? sym;
      return <div key={sym} className="flex items-center gap-1.5 shrink-0"><span className="text-gray-500 font-medium">{label}</span><span className="text-white font-mono">{data ? formatTickerPrice(data.price) : "--"}</span>{data && !isNaN(data.changePercent) && data.changePercent !== 0 && <span className={`font-mono ${data.changePercent >= 0 ? "text-emerald-400" : "text-red-400"}`}>{data.changePercent >= 0 ? "+" : ""}{data.changePercent.toFixed(2)}%</span>}</div>;
    });
    $[7] = mounted;
    $[8] = prices;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== t9) {
    t10 = <div className="border-b border-[hsl(var(--border))]/50 bg-[hsl(230,15%,4%)]/95 backdrop-blur-md overflow-hidden"><div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8"><div className="flex items-center gap-4 sm:gap-6 py-2 overflow-x-auto scrollbar-hide text-xs">{t9}</div></div></div>;
    $[10] = t9;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  const t11 = `border-b transition-all duration-200 ${scrolled ? "border-[hsl(var(--border))] bg-[hsl(230,15%,5%)]/95 backdrop-blur-xl shadow-lg shadow-black/20" : "border-transparent bg-transparent"}`;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <div className="w-8 h-8 shrink-0 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400 flex items-center justify-center shadow-lg shadow-blue-500/20"><svg className="w-4.5 h-4.5 text-white" fill="none" viewBox="0 0 24 24" strokeWidth={2.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75 22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3-4.5 16.5" /></svg></div>;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Link href="/" className="flex items-center gap-2.5 shrink-0 min-h-[44px]">{t12}<span className="text-lg font-bold tracking-tight hidden sm:inline"><span className="text-white">Developer</span><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">Activity</span></span></Link>;
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  let t14;
  if ($[14] !== pathname) {
    t14 = navLinks.map(link => {
      const isActive = pathname === link.href || pathname?.startsWith(link.href + "/");
      return <Link key={link.href} href={link.href} className={`flex items-center gap-1.5 min-h-[44px] px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${isActive ? "bg-blue-500/15 text-blue-400" : "text-gray-400 hover:text-white hover:bg-white/5"}`}><svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d={link.icon} /></svg>{link.label}</Link>;
    });
    $[14] = pathname;
    $[15] = t14;
  } else {
    t14 = $[15];
  }
  let t15;
  if ($[16] !== t14) {
    t15 = <div className="hidden lg:flex items-center gap-1">{t14}</div>;
    $[16] = t14;
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] !== isAdmin || $[19] !== rightSlot || $[20] !== session) {
    t16 = rightSlot ? rightSlot : session ? <UserMenu session={session} isAdmin={isAdmin} /> : <div className="flex items-center gap-2"><Link href="/auth/signin" className="hidden sm:inline-flex items-center justify-center min-h-[44px] px-3.5 py-2 text-gray-400 hover:text-white text-sm font-medium transition-colors">Sign In</Link><Link href="/auth/signup" className="inline-flex items-center justify-center min-h-[44px] px-3.5 py-2 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white rounded-lg text-sm font-medium transition-all shadow-lg shadow-blue-600/20 whitespace-nowrap">Get Started</Link></div>;
    $[18] = isAdmin;
    $[19] = rightSlot;
    $[20] = session;
    $[21] = t16;
  } else {
    t16 = $[21];
  }
  const t17 = mobileOpen ? "Close menu" : "Open menu";
  let t18;
  if ($[22] !== mobileOpen) {
    t18 = () => setMobileOpen(!mobileOpen);
    $[22] = mobileOpen;
    $[23] = t18;
  } else {
    t18 = $[23];
  }
  let t19;
  if ($[24] !== mobileOpen) {
    t19 = <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">{mobileOpen ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5" />}</svg>;
    $[24] = mobileOpen;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== mobileOpen || $[27] !== t17 || $[28] !== t18 || $[29] !== t19) {
    t20 = <button type="button" aria-label={t17} aria-expanded={mobileOpen} className="lg:hidden inline-flex items-center justify-center h-11 w-11 shrink-0 text-gray-400 hover:text-white rounded-lg hover:bg-white/5 transition-colors" onClick={t18}>{t19}</button>;
    $[26] = mobileOpen;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
  } else {
    t20 = $[30];
  }
  let t21;
  if ($[31] !== t16 || $[32] !== t20) {
    t21 = <div className="flex items-center gap-2 shrink-0">{t16}{t20}</div>;
    $[31] = t16;
    $[32] = t20;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t15 || $[35] !== t21) {
    t22 = <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8"><div className="flex items-center justify-between gap-2 h-14">{t13}{t15}{t21}</div></div>;
    $[34] = t15;
    $[35] = t21;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== isAdmin || $[38] !== mobileOpen || $[39] !== pathname || $[40] !== session) {
    t23 = mobileOpen && <div className="lg:hidden border-t border-[hsl(var(--border))] bg-[hsl(230,15%,5%)]/98 backdrop-blur-xl max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain"><div className="max-w-[1400px] mx-auto px-4 py-4 space-y-1">{navLinks.map(link_0 => {
          const isActive_0 = pathname === link_0.href;
          return <Link key={link_0.href} href={link_0.href} className={`flex items-center gap-3 min-h-[48px] px-4 py-3 rounded-xl text-sm font-medium transition-all ${isActive_0 ? "bg-blue-500/15 text-blue-400" : "text-gray-400 hover:text-white hover:bg-white/5"}`}><svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d={link_0.icon} /></svg>{link_0.label}</Link>;
        })}<div className="pt-3 border-t border-[hsl(var(--border))]">{session ? <div className="space-y-2"><Link href="/dashboard" className="flex items-center justify-center gap-2 min-h-[48px] px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium">Dashboard</Link><Link href="/settings" className="flex items-center justify-center min-h-[48px] px-4 py-3 text-gray-300 hover:text-white rounded-xl text-sm font-medium transition-colors">Settings</Link>{isAdmin ? <Link href="/admin" className="flex items-center justify-center min-h-[48px] px-4 py-3 text-gray-300 hover:text-white rounded-xl text-sm font-medium transition-colors">Admin</Link> : null}<form action="/api/auth/logout" method="POST"><button type="submit" className="w-full min-h-[48px] text-center px-4 py-3 text-red-400 hover:text-red-300 rounded-xl text-sm font-medium transition-colors">Sign out</button></form></div> : <div className="space-y-2"><Link href="/auth/signin" className="flex items-center justify-center min-h-[48px] px-4 py-3 text-gray-400 hover:text-white rounded-xl text-sm font-medium transition-colors">Sign In</Link><Link href="/auth/signup" className="flex items-center justify-center min-h-[48px] px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl text-sm font-medium">Get Started</Link></div>}</div></div></div>;
    $[37] = isAdmin;
    $[38] = mobileOpen;
    $[39] = pathname;
    $[40] = session;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  let t24;
  if ($[42] !== t11 || $[43] !== t22 || $[44] !== t23) {
    t24 = <nav className={t11}>{t22}{t23}</nav>;
    $[42] = t11;
    $[43] = t22;
    $[44] = t23;
    $[45] = t24;
  } else {
    t24 = $[45];
  }
  let t25;
  if ($[46] !== t10 || $[47] !== t24) {
    t25 = <header className="sticky top-0 z-50">{t10}{t24}</header>;
    $[46] = t10;
    $[47] = t24;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  return t25;
}